import { useState, useCallback, useRef } from 'react'

export type ToastType = 'success' | 'error' | 'info'

export interface ToastAction {
  label: string
  onAction: () => void
}

export interface Toast {
  id: number
  type: ToastType
  message: string
  action?: ToastAction
  duration: number
  dismissing?: boolean
}

const MAX_TOASTS = 2
const DEFAULT_DURATION = 4000

let nextId = 1

export function useToast(duration = DEFAULT_DURATION) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map())

  const dismiss = useCallback((id: number) => {
    // Start dismiss animation
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, dismissing: true } : t)))
    // Remove after animation completes
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, 200)
    const timer = timersRef.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timersRef.current.delete(id)
    }
  }, [])

  const showToast = useCallback(
    (type: ToastType, message: string, action?: ToastAction) => {
      const id = nextId++
      const toastDuration = action ? duration + 2000 : duration
      setToasts((prev) => {
        const next = [...prev, { id, type, message, action, duration: toastDuration }]
        // Remove oldest if over limit
        if (next.length > MAX_TOASTS) {
          const removed = next.shift()
          if (removed) {
            const timer = timersRef.current.get(removed.id)
            if (timer) {
              clearTimeout(timer)
              timersRef.current.delete(removed.id)
            }
          }
        }
        return next
      })

      const timer = setTimeout(() => {
        dismiss(id)
        timersRef.current.delete(id)
      }, toastDuration)
      timersRef.current.set(id, timer)
    },
    [duration, dismiss],
  )

  return { toasts, showToast, dismiss }
}
